import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useState } from "react";
import {
  Shield,
  Wifi,
  ArrowRight,
  Eye,
  Lock,
  CheckCircle2,
  Play,
  Zap,
  Battery,
  ArrowLeftRight,
  Music,
  Bell,
  Video,
  Volume2,
  SwitchCamera,
  Moon,
  WifiOff,
  Signal,
  Smartphone,
  ChevronDown,
} from "lucide-react";
import { CookieBanner } from "../components/CookieBanner";
import styles from "./HomePage.module.scss";

// JSON-LD Structured Data
const softwareApplicationSchema = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "GoodBaby",
  description:
    "A privacy-first, peer-to-peer baby monitor that runs entirely in your browser. No cloud, no subscriptions, just peace of mind.",
  applicationCategory: "UtilitiesApplication",
  operatingSystem: "Web, iOS, Android",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
  },
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5",
    ratingCount: "1",
  },
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "GoodBaby",
  url: "https://goodbaby.cam",
  logo: "https://goodbaby.cam/logo.png",
  sameAs: [],
};

const faqData = [
  {
    question: "Is GoodBaby really free?",
    answer:
      "Yes! GoodBaby is completely free to use. There are no hidden fees, no subscriptions, and no premium tiers. We believe every parent deserves peace of mind without a price tag.",
  },
  {
    question: "What devices can I use as a baby monitor?",
    answer:
      "Any device with a modern browser, camera, and microphone. This includes smartphones (iPhone, Android), tablets (iPad, Android tablets), laptops, and desktop computers with webcams.",
  },
  {
    question: "How secure is the video stream?",
    answer:
      "Extremely secure. We use industry-standard DTLS/SRTP encryption, the same technology used by major video conferencing platforms. Your video streams directly between your devices (peer-to-peer) and never touches our servers.",
  },
  {
    question: "Does GoodBaby work outside my home network?",
    answer:
      "Yes! GoodBaby works anywhere you have internet. Monitor your baby from grandma's house, on vacation, or even from across the world.",
  },
  {
    question: "Do I need to install an app?",
    answer:
      "No app installation required. GoodBaby runs entirely in your web browser. However, you can 'Add to Home Screen' on mobile devices for an app-like experience as a Progressive Web App (PWA).",
  },
  {
    question: "What happens if the baby device loses battery?",
    answer:
      "GoodBaby shows you the baby device's battery level in real-time. You'll receive an alert when the battery is low, and a critical notification if the device goes offline unexpectedly.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const reviewsData = [
  {
    name: "Sarah M.",
    country: "USA",
    flag: "🇺🇸",
    review:
      "Finally something that actually works.I was so tired of amazon cheap monitors losing signal every 10 minutes. This just works, and the video quality is way better.",
  },
  {
    name: "James T.",
    country: "New Zealand",
    flag: "🇳🇿",
    review:
      "I use goodbaby when we go out of the house. Didn't have to setup any extra system, just opened it on my old phone. Brilliant.",
  },
  {
    name: "Emma L.",
    country: "Australia",
    flag: "🇦🇺",
    review:
      "The fact that it's encrypted and doesn't store anything on some random server was the selling point for us for peace of mind.",
  },
  {
    name: "Tom B.",
    country: "United Kingdom",
    flag: "🇬🇧",
    review:
      "We've got twins and honestly this has been a lifesaver. Motion alerts mean I'm not glued to the screen all night.",
  },
  {
    name: "Michelle R.",
    country: "Canada",
    flag: "🇨🇦",
    review:
      "I was skeptical at first - how can a free app be any good? But wow, it's genuinely better than the paid app we tried before.",
  },
  {
    name: "Markus W.",
    country: "Germany",
    flag: "🇩🇪",
    review:
      "Works perfectly between our old iPad in the nursery and my phone. Setup took 1 minute. Very impressed with the engineering.",
  },
];

export function HomePage() {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className={styles.page}>
      <Helmet>
        <title>
          GoodBaby - Free, Secure & Smart Baby Monitor | No Apps, No Hardware
        </title>
        <meta
          name="description"
          content="Turn your phone or tablet into a secure baby monitor instantly. Free, encrypted, peer-to-peer video streaming. No hardware to buy, no apps to install."
        />
        <meta
          name="keywords"
          content="baby monitor, free baby monitor, p2p baby monitor, secure baby monitor, wifi baby monitor, old phone baby monitor"
        />
        <link rel="canonical" href="https://goodbaby.cam/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://goodbaby.cam/" />
        <meta
          property="og:title"
          content="GoodBaby - Turn Any Device into a Secure Baby Monitor"
        />
        <meta
          property="og:description"
          content="Turn your phone or tablet into a secure baby monitor instantly. Free, encrypted, peer-to-peer video streaming."
        />
        <meta property="og:image" content="https://goodbaby.cam/og-image.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://goodbaby.cam/" />
        <meta
          property="twitter:title"
          content="GoodBaby - Turn Any Device into a Secure Baby Monitor"
        />
        <meta
          property="twitter:description"
          content="Turn your old phone or tablet into a secure baby monitor instantly. Free, encrypted, peer-to-peer video streaming."
        />
        <meta
          property="twitter:image"
          content="https://goodbaby.cam/og-image.png"
        />

        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(softwareApplicationSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationSchema)}
        </script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.heroContent}>
          <div className={styles.heroBadge}>
            <Shield size={14} strokeWidth={2.5} />
            <span>Private & Encrypted Baby Monitor</span>
          </div>

          <h1 className={styles.heroTitle}>
            Turn Your Phone into
            <br />
            <span className={styles.highlight}>Smart & Secure</span> Baby
            Monitor.
          </h1>

          <p className={styles.heroSubtitle}>
            with smart motion detection, remote lullabies, on device recording
            and much more.
          </p>

          <div className={styles.heroCta}>
            <Link to="/signup" className={styles.primaryButton}>
              <span>Get Started Free</span>
              <ArrowRight size={20} strokeWidth={2.5} />
            </Link>
            <Link to="/login" className={styles.secondaryButton}>
              <Play size={18} strokeWidth={2.5} />
              <span>Sign In</span>
            </Link>
          </div>

          <div className={styles.trustBadge}>
            <CheckCircle2 size={24} className="text-green-600" />
            <div className={styles.trustText}>
              <strong>100% Encrypted & Private</strong>
              <span>WEBRTC • DTLS/SRTP • P2P</span>
            </div>
          </div>
        </div>
      </section>

      {/* Hero Features Grid */}
      <section className={styles.features}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>
            Why Parents Choose GoodBaby
          </span>
          <h2 className={styles.sectionTitle}>
            Premium Features. <br />
            Zero Monthly Fees.
          </h2>
        </div>

        <div className={styles.featureGrid}>
          {/* Feature: Private & Encrypted */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconTerracotta}`}>
              <Shield size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Private & Encrypted</h3>
            <p className={styles.featureDesc}>
              Your video never touches our servers - it streams directly between
              your devices using peer-to-peer technology with industry-grade
              DTLS/SRTP encryption.
            </p>
          </div>

          {/* Feature: Use Any Device */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconYellow}`}>
              <Zap size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Use Any Device</h3>
            <p className={styles.featureDesc}>
              Turn your spare phone, tablet, or laptop into a HD baby monitor.
              No expensive hardware needed.
            </p>
          </div>

          {/* Feature: Unlimited Range */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconMint}`}>
              <Wifi size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Unlimited Range</h3>
            <p className={styles.featureDesc}>
              Works anywhere with internet. Monitor from the backyard, office,
              or across the world.
            </p>
          </div>

          {/* Feature: Motion Detection */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconYellow}`}>
              <Eye size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Smart Motion Alerts</h3>
            <p className={styles.featureDesc}>
              Intelligent motion detection notifies you the second your baby
              stirs. Reliable, secure math - no cloud AI.
            </p>
          </div>

          {/* Feature: Remote Lullabies */}
          <div className={`${styles.featureCard} ${styles.lullabyCard}`}>
            <div className={`${styles.featureIcon} ${styles.iconLavender}`}>
              <Music size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Remote Lullabies</h3>
            <p className={styles.featureDesc}>
              Soothe your baby from anywhere by playing calming sounds directly
              through the device.
            </p>
          </div>

          {/* Feature: Record Memories */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconMint}`}>
              <Video size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Record Memories</h3>
            <p className={styles.featureDesc}>
              Save precious moments directly to your device. No cloud
              subscriptions, no data limits, 100% private.
            </p>
          </div>

          {/* Feature: Audio-Only Mode */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconYellow}`}>
              <Volume2 size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>Audio-Only Mode</h3>
            <p className={styles.featureDesc}>
              Save battery by streaming audio only. Perfect for overnight
              monitoring when video isn't needed.
            </p>
          </div>

          {/* Feature: Cry Detection */}
          <div className={styles.featureCard}>
            <div className={`${styles.featureIcon} ${styles.iconTerracotta}`}>
              <Bell size={28} strokeWidth={2} />
            </div>
            <h3 className={styles.featureTitle}>
              Cry Detection
              <span className={styles.commingSoonBadge}>Coming Soon</span>
            </h3>
            <p className={styles.featureDesc}>
              Advanced audio analysis to detect crying specific frequencies is
              currently in beta.
            </p>
          </div>
        </div>
      </section>

      {/* More Features Section */}
      <section className={styles.moreFeatures}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>And So Much More</span>
          <h2 className={styles.sectionTitle}>Everything You Need</h2>
        </div>

        <div className={styles.moreFeatureGrid}>
          {/* Battery Monitoring */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <Battery size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Battery Monitoring</h4>
              <p className={styles.moreFeatureDesc}>
                See baby device battery level, get low-battery alerts
              </p>
            </div>
          </div>

          {/* Camera Switching */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <SwitchCamera size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Camera Switching</h4>
              <p className={styles.moreFeatureDesc}>
                Switch between front, back, side cameras, whatever your device
                has.
              </p>
            </div>
          </div>

          {/* Screen Blinds */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <Moon size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Screen Blinds</h4>
              <p className={styles.moreFeatureDesc}>
                Black out the baby device screen to avoid disturbing sleep
              </p>
            </div>
          </div>

          {/* Disconnect Alerts */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <WifiOff size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Disconnect Alerts</h4>
              <p className={styles.moreFeatureDesc}>
                Critical alert if baby device loses connection or battery dies
              </p>
            </div>
          </div>

          {/* Connection Quality */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <Signal size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Connection Quality</h4>
              <p className={styles.moreFeatureDesc}>
                Live indicator showing WiFi strength and stream quality
              </p>
            </div>
          </div>

          {/* Haptic Feedback */}
          <div className={styles.moreFeatureCard}>
            <div className={styles.moreFeatureIcon}>
              <Smartphone size={20} strokeWidth={2} />
            </div>
            <div className={styles.moreFeatureContent}>
              <h4 className={styles.moreFeatureTitle}>Haptic Feedback</h4>
              <p className={styles.moreFeatureDesc}>
                Vibration alerts on your phone for detected motion and
                connection issues
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className={styles.howItWorks}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Simple Setup</span>
          <h2 className={styles.sectionTitle}>Up and Running in 60 Seconds</h2>
        </div>

        <div className={styles.steps}>
          <div className={styles.stepCard}>
            <div className={styles.stepNumber}>1</div>
            <h3 className={styles.stepTitle}>Sign Up Free</h3>
            <p className={styles.stepText}>
              Create a secure account. No credit card required.
            </p>
          </div>
          <div className={styles.stepCard}>
            <div className={styles.stepNumber}>2</div>
            <h3 className={styles.stepTitle}>Select Mode</h3>
            <p className={styles.stepText}>
              Open goodbaby website on two devices. Set one as <b>Baby</b>, one
              as <b>Parent</b>.
            </p>
          </div>
          <div className={styles.stepCard}>
            <div className={styles.stepNumber}>3</div>
            <h3 className={styles.stepTitle}>Instant Link</h3>
            <p className={styles.stepText}>
              Devices auto-discover each other and start the secure stream.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className={styles.faqSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>FAQ</span>
          <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
        </div>

        <div className={styles.faqList}>
          {faqData.map((faq, index) => (
            <div
              key={index}
              className={`${styles.faqItem} ${openFaqIndex === index ? styles.faqItemOpen : ""}`}
            >
              <button
                className={styles.faqQuestion}
                onClick={() => toggleFaq(index)}
                aria-expanded={openFaqIndex === index}
              >
                <span>{faq.question}</span>
                <ChevronDown
                  size={20}
                  className={styles.faqChevron}
                  strokeWidth={2}
                />
              </button>
              <div className={styles.faqAnswer}>
                <p>{faq.answer}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Reviews Section */}
      <section className={styles.reviewsSection}>
        <div className={styles.sectionHeader}>
          <span className={styles.sectionBadge}>Loved Worldwide</span>
          <h2 className={styles.sectionTitle}>What Parents Are Saying</h2>
        </div>

        <div className={styles.reviewsGrid}>
          {reviewsData.map((review, index) => (
            <div key={index} className={styles.reviewCard}>
              <div className={styles.reviewStars}>{"★★★★★"}</div>
              <p className={styles.reviewText}>"{review.review}"</p>
              <div className={styles.reviewAuthor}>
                <span className={styles.reviewFlag}>{review.flag}</span>
                <div className={styles.reviewInfo}>
                  <span className={styles.reviewName}>{review.name}</span>
                  <span className={styles.reviewCountry}>{review.country}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Security Deep Dive - Redesigned */}
      <section className={styles.securitySection}>
        <div className={styles.securityContainer}>
          <div className={styles.securityHeader}>
            <span className={styles.sectionBadge}>Uncompromised Security</span>
            <h2 className={styles.sectionTitle}>
              Your Privacy is Our Priority
            </h2>
            <p className={styles.sectionSubtitle}>
              We use industry-standard encryption to ensure your video feed
              remains private and secure.
            </p>
          </div>

          <div className={styles.securityCards}>
            <div className={styles.securityCard}>
              <div className={styles.securityIconBox}>
                <Lock size={32} strokeWidth={1.5} />
              </div>
              <h3>End-to-End Encrypted</h3>
              <p>
                All video & audio is encrypted via DTLS/SRTP. Even we can't see
                your video.
              </p>
            </div>

            <div className={styles.securityCard}>
              <div className={styles.securityIconBox}>
                <ArrowLeftRight size={32} strokeWidth={1.5} />
              </div>
              <h3>Peer-to-Peer</h3>
              <p>
                Video flows directly between devices. No cloud recording, no
                data harvesting.
              </p>
            </div>

            <div className={styles.securityCard}>
              <div className={styles.securityIconBox}>
                <Shield size={32} strokeWidth={1.5} />
              </div>
              <h3>Secure Standard</h3>
              <p>
                Built on WebRTC standards used by major secure communication
                platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Builder's Note */}
      <section className={styles.buildersNote}>
        <div className={styles.noteContainer}>
          <div className={styles.noteContent}>
            <h2 className={styles.noteTitle}>
              Built by a parent, for parents.
            </h2>
            <p className={styles.noteText}>
              "I built GoodBaby because I wasn't satisfied with the cheap
              plastic monitors with bad camera quality and apps thats have
              terrible security and hard to use. I use goodbaby.cam every day to
              monitor my own baby boy. It had to be perfect, secure, and
              reliable. And now it is."
            </p>
            <div className={styles.noteAuthor}>
              <div className={styles.authorInfo}>
                <span className={styles.authorName}>Nakul Rathore</span>
                <span className={styles.authorRole}>Founder & Dad</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={styles.finalCta}>
        <div className={styles.ctaContent}>
          <h2>Ready for peace of mind?</h2>
          <p>Join hundreds of modern parents trusting GoodBaby.</p>
          <Link to="/signup" className={styles.finalCtaButton}>
            <span>Start Monitoring</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Minimal Footer */}
      <footer className={styles.minimalFooter}>
        <div className={styles.footerContent}>
          <p className={styles.copyright}>
            © {new Date().getFullYear()} GoodBaby. All rights reserved.
          </p>
          <div className={styles.footerLinks}>
            <Link to="/support">Support</Link>
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
          </div>
        </div>
      </footer>

      {/* Cookie Banner */}
      <CookieBanner />
    </div>
  );
}
